import { useQuery } from '@tanstack/react-query';
import type { DashboardWidgetKey } from '@efacture/core/sector';
import { apiClient } from '@/api/client';
import type { ClientStatistics } from '@/api/queries/clients';
import type { ArticleStatistics, Article } from '@/api/queries/articles';
import type { DecimalString } from '@efacture/web-billing/lib/decimal';

// ─────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────

export interface DashboardData {
  clientStats: ClientStatistics;
  articleStats: ArticleStatistics;
  lowStockArticles: Article[];
  invoicesThisMonth: number;
}

export interface DashboardInvoiceStats {
  invoicesThisMonth: number;
  overdueInvoicesCount: number;
  recentInvoices: {
    id: string;
    number: string;
    clientName: string | null;
    totalTTC: DecimalString;
    /** Stored currency of the invoice: the currency of `totalTTC`. */
    currency: string;
    status: string;
    createdAt: string;
  }[];
}

/**
 * The blocks `/dashboard/summary` returns, one optional entry per key.
 *
 * A key is ABSENT when the server refused it (the reader lacks the
 * permission its data answers to), when the module behind it is off, or
 * when it has nothing to stand on — a till that was never opened. The
 * component renders nothing at all in each case, which is why no shape
 * below carries a « no data » variant.
 */
/** The revenue of the window in one currency. */
export interface RevenueTotals {
  currency: string;
  totalHT: number;
  totalTVA: number;
  totalTTC: number;
  invoiceCount: number;
}

/** The receivables in one currency. */
export interface ReceivableTotals {
  currency: string;
  totalRemaining: number;
  totalCount: number;
  /** A SUBSET of the two figures above, from the same scan. */
  overdueCount: number;
  overdueRemaining: number;
}

/** The top articles of the window, ranked within one currency. */
export interface TopArticlesRanking {
  currency: string;
  articles: { id: string; name: string; quantity: number; total: number }[];
}

/** One operator's sales in one currency. */
export interface OperatorSalesTotals {
  currency: string;
  periodTotalTTC: number;
  periodInvoiceCount: number;
  todayTotalTTC: number;
  todayInvoiceCount: number;
}

/**
 * The sales blocks are folded per stored invoice currency: the top-level
 * figures are in `currency`, the tenant's working currency today, and the
 * invoices in any other currency are reported apart in `otherCurrencies`,
 * never added to them. Every other amount block is a ledger, payroll,
 * budget or till aggregate with no currency of its own.
 */
export interface DashboardSummaryBlocks {
  'kpi-revenue'?: RevenueTotals & { otherCurrencies: RevenueTotals[] };
  'kpi-receivables'?: ReceivableTotals & {
    invoices: {
      id: string;
      number: string;
      clientName: string;
      /** Stored currency of the invoice: the currency of `remaining`. */
      currency: string;
      remaining: number;
    }[];
    otherCurrencies: ReceivableTotals[];
  };
  'top-articles'?: TopArticlesRanking & { otherCurrencies: TopArticlesRanking[] };
  'my-sales'?: OperatorSalesTotals & { otherCurrencies: OperatorSalesTotals[] };
  'till-today'?: {
    sessionId: string;
    openedAt: string;
    openingBalance: number;
    totalSales: number;
    totalDeposits: number;
    totalWithdrawals: number;
    totalRefunds: number;
    expectedBalance: number;
    transactionCount: number;
  };
  'pending-tickets'?: {
    total: number;
    suspended: number;
    byStatus: Record<string, number>;
  };
  'stock-overview'?: {
    totalArticles: number;
    stockManagedArticles: number;
    lowStockCount: number;
    expiringCount: number;
    totalValue: number;
  };
  'near-expiry'?: {
    total: number;
    batches: {
      id: string;
      batchNumber: string;
      articleName: string;
      currentQty: number;
      expirationDate: string | null;
      daysUntilExpiration: number;
    }[];
  };
  'stock-movements-today'?: { total: number; byType: Record<string, number> };
  'verification-queue'?: { pending: number; oldestSubmittedAt: string | null };
  'mcf-doubt'?: { total: number };
  treasury?: { bank: number; cash: number; total: number };
  'kpi-margin'?: {
    /** The working currency: the currency of the revenue leg and of the margin. */
    currency: string;
    revenueHT: number;
    cogs: number;
    margin: number;
    marginRate: number | null;
    indicative: boolean;
  };
  'accounting-health'?: {
    year: number;
    draftEntries: number;
    outboxPending: number;
    outboxFailed: number;
    periodsLocked: { locked: number; total: number };
    /** `null` when the exercise holds no entry: nothing to balance. */
    balanced: boolean | null;
    entryCount: number;
    ready: boolean;
    exerciseStatus: string | null;
    openPeriodMonth: number | null;
  };
  'hr-headcount'?: { active: number; byContractType: Record<string, number> };
  'hr-leave'?: { pending: number; approvedDaysThisYear: number };
  'hr-contracts'?: {
    total: number;
    employees: {
      id: string;
      name: string;
      contractType: string;
      endDate: string | null;
    }[];
  };
  'payroll-status'?: {
    year: number;
    byStatus: Record<string, number>;
    totalGross: number;
    totalNet: number;
    totalEmployerCharges: number;
  };
  'budget-execution'?: {
    year: number;
    envelopeCount: number;
    engaged: number;
    liquidated: number;
    ordered: number;
    paid: number;
  };
  'budget-queues'?: {
    year: number;
    awaitingVisa: number;
    publicExecution: boolean;
    toOrder: number;
    toPay: number;
  };
  establishments?: { total: number; active: number };
}

export interface DashboardSummary {
  blocks: DashboardSummaryBlocks;
  period: { from: string; until: string };
  generatedAt: string;
  /** Blocks whose computation failed — asked for, permitted, not served. */
  failed: string[];
}

// ─────────────────────────────────────────────────────────────
// Query Keys
// ─────────────────────────────────────────────────────────────

export const dashboardKeys = {
  all: ['dashboard'] as const,
  data: () => [...dashboardKeys.all, 'data'] as const,
  invoiceStats: () => [...dashboardKeys.all, 'invoiceStats'] as const,
  clientStats: () => [...dashboardKeys.all, 'clientStats'] as const,
  articleStats: () => [...dashboardKeys.all, 'articleStats'] as const,
  lowStock: () => [...dashboardKeys.all, 'lowStock'] as const,
  // The requested set is part of the key: two readers with different
  // roles ask for different blocks and must not share one cache entry.
  summary: (widgets: readonly string[]) =>
    [...dashboardKeys.all, 'summary', [...widgets].sort().join(',')] as const,
};

// ─────────────────────────────────────────────────────────────
// API Functions
// ─────────────────────────────────────────────────────────────

async function fetchClientStatistics(): Promise<ClientStatistics> {
  return apiClient.get<ClientStatistics>('/clients/statistics');
}

async function fetchArticleStatistics(): Promise<ArticleStatistics> {
  return apiClient.get<ArticleStatistics>('/articles/statistics');
}

async function fetchLowStockArticles(): Promise<Article[]> {
  return apiClient.get<Article[]>('/articles/low-stock');
}

async function fetchDashboardInvoiceStats(): Promise<DashboardInvoiceStats> {
  return apiClient.get<DashboardInvoiceStats>('/reports/dashboard-stats');
}

async function fetchDashboardSummary(
  widgets: readonly string[]
): Promise<DashboardSummary> {
  return apiClient.get<DashboardSummary>(
    `/dashboard/summary?widgets=${encodeURIComponent(widgets.join(','))}`
  );
}

// ─────────────────────────────────────────────────────────────
// Queries
// ─────────────────────────────────────────────────────────────

/**
 * Hook to fetch client statistics for dashboard
 * GET /api/clients/statistics
 */
export function useDashboardClientStats(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: dashboardKeys.clientStats(),
    queryFn: fetchClientStatistics,
    staleTime: 1000 * 60 * 5, // 5 minutes
    enabled: options?.enabled ?? true,
  });
}

/**
 * Hook to fetch article statistics for dashboard
 * GET /api/articles/statistics
 */
export function useDashboardArticleStats(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: dashboardKeys.articleStats(),
    queryFn: fetchArticleStatistics,
    staleTime: 1000 * 60 * 5, // 5 minutes
    enabled: options?.enabled ?? true,
  });
}

/**
 * Hook to fetch low stock articles for dashboard
 * GET /api/articles/low-stock
 */
export function useDashboardLowStock(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: dashboardKeys.lowStock(),
    queryFn: fetchLowStockArticles,
    staleTime: 1000 * 60 * 2, // 2 minutes
    enabled: options?.enabled ?? true,
  });
}

/**
 * Hook to fetch invoice stats for dashboard
 * GET /api/reports/dashboard-stats
 */
export function useDashboardInvoiceStats(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: dashboardKeys.invoiceStats(),
    queryFn: fetchDashboardInvoiceStats,
    staleTime: 1000 * 60 * 2, // 2 minutes
    enabled: options?.enabled ?? true,
  });
}

/**
 * Every summary-backed block of this reader's dashboard, in one request.
 *
 * The list sent is what the role registry resolved and the permission
 * layer kept; the server checks each key again on its own permission and
 * returns the subset it may serve. Sending a key the reader turns out not
 * to hold costs nothing — it comes back absent, not as an error.
 */
export function useDashboardSummary(
  widgets: readonly DashboardWidgetKey[],
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: dashboardKeys.summary(widgets),
    queryFn: () => fetchDashboardSummary(widgets),
    staleTime: 1000 * 60 * 2, // 2 minutes
    enabled: (options?.enabled ?? true) && widgets.length > 0,
  });
}

// ─────────────────────────────────────────────────────────────
// Utility Exports
// ─────────────────────────────────────────────────────────────

export {
  fetchClientStatistics,
  fetchArticleStatistics,
  fetchLowStockArticles,
  fetchDashboardInvoiceStats,
  fetchDashboardSummary,
};
